import type { QueryClient } from '@tanstack/react-query';
import { playMessageSound } from '@/shared/sound/playMessageSound';
import { queryKeys } from '@/shared/query';
import type { InternalChatMessageCreatedPayload } from '@/shared/realtime/types';
import type { InternalChatThread } from '../types';

export function isViewingInternalChatThread(threadId: string): boolean {
  if (typeof document === 'undefined' || typeof window === 'undefined') return false;
  if (document.visibilityState !== 'visible') return false;
  if (!window.location.pathname.startsWith('/internal-chat')) return false;
  return new URLSearchParams(window.location.search).get('t') === threadId;
}

function findThreadInCache(
  queryClient: QueryClient,
  threadId: string
): InternalChatThread | undefined {
  const threadQueries = queryClient.getQueriesData<InternalChatThread[]>({
    queryKey: ['internalChat', 'threads'],
  });
  for (const [, data] of threadQueries) {
    if (!Array.isArray(data)) continue;
    const match = data.find((row) => row.id === threadId);
    if (match) return match;
  }
  return queryClient.getQueryData<InternalChatThread>(queryKeys.internalChat.thread(threadId));
}

export function isViewerMentionedInMessage(
  payload: InternalChatMessageCreatedPayload,
  viewerUserId: string | undefined
): boolean {
  if (!viewerUserId) return false;
  if (payload.mentionAll) return true;
  return (payload.mentionUserIds ?? []).includes(viewerUserId);
}

export function shouldPlayInternalChatMessageSound(
  queryClient: QueryClient,
  payload: InternalChatMessageCreatedPayload,
  viewerUserId: string | undefined
): boolean {
  const threadId = payload?.threadId;
  if (!threadId || payload.created !== true) return false;
  if (isViewingInternalChatThread(threadId)) return false;

  const mentioned = isViewerMentionedInMessage(payload, viewerUserId);
  const thread = findThreadInCache(queryClient, threadId);
  if (thread?.muted && !mentioned) return false;

  return true;
}

export function tryPlayInternalChatMessageSound(
  queryClient: QueryClient,
  payload: InternalChatMessageCreatedPayload,
  viewerUserId: string | undefined
): void {
  if (!shouldPlayInternalChatMessageSound(queryClient, payload, viewerUserId)) return;
  playMessageSound();
}
